import { isEmpty } from 'lodash'

/**
 * Campaign-name token sources for Intermedia Unite.
 *
 * Mirrors TCN's shape, minus its per-payload prefixes: Intermedia delivers one
 * webhook payload, so there is one path source rather than four. Kept in sync
 * with the CAMPAIGN_* constants in
 * src/connectors/intermedia_unite/intermedia_unite.py.
 */

// Tokens the backend resolves on its own, so choosing one leaves nothing to type.
// `direction` is DERIVED from which party carries an email, not read from a field
// -- payload.callDirection is the raw vendor value ("incoming"/"outgoing", and
// "undefined" on call-finished), while this is the inbound/outbound already sent
// to Balto. `dialedDid` is two paths behind one token (originalTo.phoneNumber
// falling back to xFeature.unknownParams.original_to_did). Neither is expressible
// as a single path, which is why both are named tokens.
export const WHOLE_VALUE_TOKENS = ['direction', 'huntGroup', 'dialedDid']

export const isWholeValuePrefix = (prefix) => WHOLE_VALUE_TOKENS.includes(prefix)

export const PATH_PREFIXES = ['event']

// Grouped so the dropdown separates "the whole value" from "a path into the payload".
export const PREFIX_OPTION_GROUPS = [
  {
    label: 'Whole value',
    options: [
      { value: 'dialedDid', label: 'Dialed Number (DID)' },
      { value: 'huntGroup', label: 'Hunt Group' },
      { value: 'direction', label: 'Direction' },
    ],
  },
  {
    label: 'Path into a payload',
    options: [{ value: 'event', label: 'Event Payload' }],
  },
]

const PREFIX_LABELS = PREFIX_OPTION_GROUPS.flatMap((group) => group.options).reduce(
  (acc, option) => ({ ...acc, [option.value]: option.label }),
  {}
)

export const labelForPrefix = (prefix) => PREFIX_LABELS[prefix] || prefix

// Rooted at the webhook body, matching the connector's FIELD_PATHS and the legacy
// voip_campaign_name override, so an operator who knows one knows the other.
export const PATH_EXAMPLES = {
  event: 'payload.xFeature.unknownParams.original_to_did',
}

/**
 * Split a stored token into the two controls that produced it.
 *
 * Partitions on the FIRST colon only: a path may itself contain one, e.g. inside a
 * predicate value. A whole-value token has no path.
 */
export const splitToken = (token) => {
  const trimmed = typeof token === 'string' ? token.trim() : ''
  if (isWholeValuePrefix(trimmed)) {
    return { prefix: trimmed, path: '' }
  }
  const colon = trimmed.indexOf(':')
  if (colon === -1) {
    return { prefix: '', path: '' }
  }
  return { prefix: trimmed.slice(0, colon), path: trimmed.slice(colon + 1) }
}

/** Inverse of splitToken. Must round-trip. */
export const joinToken = (prefix, path) => {
  if (!prefix) {
    return ''
  }
  if (isWholeValuePrefix(prefix)) {
    return prefix
  }
  return `${prefix}:${path || ''}`
}

/**
 * Whether a token is one the backend can resolve. A whole-value token stands
 * alone; a path source needs a non-empty path, because the resolver partitions on
 * the colon and rejects an empty one.
 */
export const isQualifiedToken = (token) => {
  const { prefix, path } = splitToken(token)
  if (isWholeValuePrefix(prefix)) {
    return true
  }
  return PATH_PREFIXES.includes(prefix) && !isEmpty(path.trim())
}
